import { format, startOfMonth, startOfYear, subDays } from 'date-fns'
import type { DailyReportMetric, GetDailyReportsData } from 'api/generated'

type Query = GetDailyReportsData['query']
export type Granularity = NonNullable<Query['granularity']>

export const PERIODS = [
  { key: 'last_7_days', label: '7 giorni' },
  { key: 'last_30_days', label: '30 giorni' },
  { key: 'last_90_days', label: '90 giorni' },
  { key: 'current_month', label: 'Mese corrente' },
  { key: 'previous_month', label: 'Mese scorso' },
  { key: 'current_year', label: 'Anno corrente' },
] as const

export type PeriodKey = (typeof PERIODS)[number]['key'] | 'custom'

export const GRANULARITIES: { key: Granularity; label: string }[] = [
  { key: 'day', label: 'Giorno' },
  { key: 'week', label: 'Settimana' },
  { key: 'month', label: 'Mese' },
]

export const AGGREGATION_LABELS: Record<
  DailyReportMetric['aggregation'],
  string
> = {
  sum: 'totale',
  last: 'ultimo valore',
  avg: 'media',
}

export interface ReportFilters {
  source: Query['source']
  period: PeriodKey
  dateFrom?: Date
  dateTo?: Date
  granularity: Granularity
}

export const toQuery = (f: ReportFilters): Query =>
  f.period === 'custom'
    ? {
        source: f.source,
        granularity: f.granularity,
        from: f.dateFrom ? format(f.dateFrom, 'yyyy-MM-dd') : undefined,
        to: f.dateTo ? format(f.dateTo, 'yyyy-MM-dd') : undefined,
      }
    : { source: f.source, granularity: f.granularity, preset: f.period }

// Seed for the custom pickers, so switching to "Su misura" keeps the window
export const presetDates = (period: PeriodKey) => {
  const today = new Date()
  switch (period) {
    case 'last_7_days':
      return { from: subDays(today, 6), to: today }
    case 'last_90_days':
      return { from: subDays(today, 89), to: today }
    case 'current_month':
      return { from: startOfMonth(today), to: today }
    case 'current_year':
      return { from: startOfYear(today), to: today }
    default:
      return { from: subDays(today, 29), to: today }
  }
}

const integer = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 })
const decimal = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 })

const duration = (seconds: number) => {
  const total = Math.round(seconds)
  if (total < 60) return `${total}s`
  const minutes = Math.floor(total / 60)
  if (minutes < 60) return `${minutes}m ${total % 60}s`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** Formats a metric value by its unit. `null` = no data. */
export const formatMetric = (
  metric: DailyReportMetric,
  value: number | null | undefined
) => {
  if (value == null) return '–'
  switch (metric.unit) {
    case 'money':
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: metric.currency ?? 'EUR',
      }).format(value)
    case 'seconds':
      return duration(value)
    case 'minutes':
      return `${integer.format(value)} min`
    case 'hours':
      return `${decimal.format(value)} h`
    case 'percent':
      return `${decimal.format(value)}%`
    default:
      // averages of counts (e.g. DAU) are fractional: show them as integers
      return integer.format(value)
  }
}

export const formatRowDate = (date: string, granularity: Granularity) => {
  const day = new Date(`${date}T00:00:00`)
  if (granularity === 'month') return format(day, 'MM/yyyy')
  if (granularity === 'week') return `Sett. ${format(day, 'dd/MM/yyyy')}`
  return format(day, 'dd/MM/yyyy')
}

/** % change of `current` over `previous`; null when there is nothing to compare. */
export const changePct = (
  current: number | null | undefined,
  previous: number | null | undefined
) =>
  current == null || previous == null || previous === 0
    ? null
    : ((current - previous) / Math.abs(previous)) * 100

export const formatPeriod = (from: string, to: string) =>
  `${format(new Date(`${from}T00:00:00`), 'dd/MM/yyyy')} – ${format(
    new Date(`${to}T00:00:00`),
    'dd/MM/yyyy'
  )}`

// Shared by the dashboard charts (recharts axes)
export const axisProps = {
  tickLine: false,
  axisLine: false,
  fontSize: 12,
  stroke: 'var(--muted-foreground)',
} as const
